/**
 * Root console: Entities Monitoring. Reachable by the root user only, so its UI text is
 * hardcoded English (not i18n) by design. Do not introduce translation keys here.
 */
import React, { useCallback, useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useSearchParams } from "react-router-dom";
import { Checkbox, Label, TextInput } from "flowbite-react";
import { AlertMessage } from "../components/atoms/AlertMessage";
import { Spinner } from "../components/atoms/Spinner";
import {
  HiCalendar,
  HiChevronDown,
  HiChevronUp,
  HiFilter,
  HiSearch,
} from "react-icons/hi";
import useAuth from "../hooks/useAuth";
import HttpService from "../services/http";
import { toast } from "react-toastify";
import PaymentModuleService, {
  PayoutAccountResponseDTO,
  BalanceResponseDTO,
  PayoutResponseDTO,
  ServicePlanDetailDTO,
  GrantPlanDTO,
  PaymentHistoryGroupedDTO,
  PlanTaxMode,
  ServiceWithPlansDTO,
  StripeTaxSettingsDTO,
  SumPlanStatusEntryDTO,
} from "../services/paymentModule";
import {
  Area,
  AreaChart,
  CartesianGrid,
  Label as ChartLabel,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import moment from "moment";
import { CustomButton } from "../components/atoms/CustomButton";
import { DataTable } from "../components/atoms/DataTable";
import { EmptyState } from "../components/atoms/EmptyState";
import ConfirmModal from "../components/public/ConfirmModal";
import { NoTranslate } from "../components/atoms/NoTranslate";
import SendPersonalMessage from "../components/pages/dashboard/entities/SendPersonalMessage";
import { useRootSendContext } from "../components/pages/dashboard/entities/sendPersonalMessage/useRootSendContext";
import { SEND_MESSAGE_TYPE } from "../constants/types";
import { HiTrash } from "react-icons/hi";
import EntityDrawer from "../components/pages/dashboard/monitoring/EntityDrawer";
import { GuaranteeRefundModal } from "../components/pages/dashboard/monitoring/GuaranteeRefundModal";
import { CancelSubscriptionModal } from "../components/pages/dashboard/monitoring/CancelSubscriptionModal";
import { GuaranteeTargetPlan } from "../components/pages/dashboard/monitoring/sumPlans";
import {
  EntityDetailModals,
  PlanManagementModal,
  DetailModalKind,
} from "../components/pages/dashboard/monitoring/EntityDetailModals";
import {
  GRANT_DAYS_DEFAULT,
  GRANT_DAYS_MAX,
  GRANT_DAYS_MIN,
  parseGrantDays,
} from "../utils/grantDuration";
import {
  EntitiesData,
  EntityDetails,
  ConnectionsStats,
  FeatureLink,
  FeatureBadges,
  OwnerEmailTarget,
  getStatusDot,
  formatDate,
  buildFeatureLinks,
} from "../components/pages/dashboard/monitoring/types";

const CHART = {
  grid: "#E5E7EB", // design-ok: gray-200, recharts veut une chaîne, pas une classe
  axis: "#6B7280", // design-ok: gray-500, recharts veut une chaîne, pas une classe
  axisLabel: "#374151", // design-ok: gray-700, recharts veut une chaîne, pas une classe
  tooltipBg: "#1F2937", // design-ok: gray-800, recharts veut une chaîne, pas une classe
  tooltipText: "#F9FAFB", // design-ok: gray-50, recharts veut une chaîne, pas une classe
  series: "#22C55E", // design-ok: green-500, recharts veut une chaîne, pas une classe
  surface: "#FFFFFF", // design-ok: white, recharts veut une chaîne, pas une classe
};

const TAX_MODE_TOASTS: Record<PlanTaxMode, string> = {
  NONE: "No tax",
  INCLUSIVE: "Tax included",
  EXCLUSIVE: "Tax on top",
};

const Monitoring: React.FC = () => {
  const { customer_identification, user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<EntitiesData | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [searchEntityId, setSearchEntityId] = useState("");
  const [entitySearchTerm, setEntitySearchTerm] = useState("");
  const [entityDetails, setEntityDetails] = useState<EntityDetails | null>(
    null
  );
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [impersonateLoading, setImpersonateLoading] = useState(false);

  const [detailModal, setDetailModal] = useState<DetailModalKind>(null);

  const [emailModalTarget, setEmailModalTarget] =
    useState<OwnerEmailTarget | null>(null);
  const [selectedEntityIds, setSelectedEntityIds] = useState<Set<string>>(
    new Set()
  );

  const [payoutAccounts, setPayoutAccounts] = useState<
    PayoutAccountResponseDTO[] | null
  >(null);
  const [payoutAccountsLoading, setPayoutAccountsLoading] = useState(false);

  const [entityBalance, setEntityBalance] = useState<BalanceResponseDTO | null>(
    null
  );
  const [entityBalanceLoading, setEntityBalanceLoading] = useState(false);

  const [entityPayouts, setEntityPayouts] = useState<
    PayoutResponseDTO[] | null
  >(null);
  const [entityPayoutsLoading, setEntityPayoutsLoading] = useState(false);

  const [serviceWithPlans, setServiceWithPlans] =
    useState<ServiceWithPlansDTO | null>(null);
  const [serviceWithPlansLoading, setServiceWithPlansLoading] = useState(false);

  const [subscriptionHistories, setSubscriptionHistories] = useState<
    PaymentHistoryGroupedDTO[] | null
  >(null);
  const [subscriptionHistoriesLoading, setSubscriptionHistoriesLoading] =
    useState(false);

  const [sumPlans, setSumPlans] = useState<SumPlanStatusEntryDTO[] | null>(
    null
  );
  const [sumPlansLoading, setSumPlansLoading] = useState(false);

  const [connectionsStats, setConnectionsStats] =
    useState<ConnectionsStats | null>(null);
  const [connectionsLoading, setConnectionsLoading] = useState(false);
  const [connectionsStartDate, setConnectionsStartDate] = useState<string>("");
  const [connectionsEndDate, setConnectionsEndDate] = useState<string>("");
  const [showConnectionsStats, setShowConnectionsStats] = useState(false);

  const [showPlanModal, setShowPlanModal] = useState(false);
  const [privatePlans, setPrivatePlans] = useState<ServicePlanDetailDTO[]>([]);
  const [privatePlansLoading, setPrivatePlansLoading] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<string>("");
  const [grantDays, setGrantDays] = useState<number>(GRANT_DAYS_DEFAULT);
  const [grantingPlan, setGrantingPlan] = useState(false);
  const [expiringPlan, setExpiringPlan] = useState(false);
  const [planToExpire, setPlanToExpire] = useState<string>("");
  const [showExpireConfirmModal, setShowExpireConfirmModal] = useState(false);
  const [showGuaranteeRefund, setShowGuaranteeRefund] = useState(false);
  const [guaranteeTargetPlan, setGuaranteeTargetPlan] =
    useState<GuaranteeTargetPlan | null>(null);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelTargetPlan, setCancelTargetPlan] =
    useState<GuaranteeTargetPlan | null>(null);
  const [cancelSubmitting, setCancelSubmitting] = useState(false);
  const [busyPlanCode, setBusyPlanCode] = useState<string | undefined>(
    undefined
  );

  const [showDeleteEntityModal, setShowDeleteEntityModal] = useState(false);
  const [deleteEntityConfirmText, setDeleteEntityConfirmText] = useState("");
  const [deletingEntity, setDeletingEntity] = useState(false);

  const [showFilters, setShowFilters] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [startDate, setStartDate] = useState<string>("");
  const [endDate, setEndDate] = useState<string>("");

  const fetchEntitiesData = useCallback(
    async (searchFilters?: {
      startDate?: string;
      endDate?: string;
      search?: string;
    }) => {
      try {
        setLoading(true);
        setError(null);

        const params = new URLSearchParams();
        params.append("page", currentPage.toString());
        params.append("limit", limit.toString());

        const filterStartDate = searchFilters?.startDate ?? startDate;
        const filterEndDate = searchFilters?.endDate ?? endDate;
        const filterSearch =
          searchFilters?.search !== undefined
            ? searchFilters.search
            : entitySearchTerm;

        if (filterStartDate)
          params.append("start_date", new Date(filterStartDate).toISOString());
        if (filterEndDate)
          params.append("end_date", new Date(filterEndDate).toISOString());
        if (filterSearch && filterSearch.trim())
          params.append("search", filterSearch.trim());

        const response = await HttpService.get(
          `/${customer_identification}/${
            user.entity_identification
          }/system/entities?${params.toString()}`
        );

        setData(response?.data?.element);
      } catch (err: any) {
        const errorMessage =
          err.response?.data?.message ||
          err.message ||
          "Failed to fetch entities data";
        setError(errorMessage);
        toast.error(errorMessage);
      } finally {
        setLoading(false);
      }
    },
    [
      customer_identification,
      user.entity_identification,
      currentPage,
      limit,
      startDate,
      endDate,
      entitySearchTerm,
    ]
  );

  const [hasInitiallyLoaded, setHasInitiallyLoaded] = useState(false);

  useEffect(() => {
    fetchEntitiesData();
    setHasInitiallyLoaded(true);
  }, []);

  useEffect(() => {
    if (hasInitiallyLoaded) {
      fetchEntitiesData();
    }
  }, [currentPage, limit]);

  const [grantOnLoad, setGrantOnLoad] = useState<string | null>(null);
  const [grantDaysOnLoad, setGrantDaysOnLoad] = useState<number | null>(null);

  useEffect(() => {
    const entityIdParam = searchParams.get("entityId");
    if (entityIdParam) {
      setGrantOnLoad(searchParams.get("grant") === "1" ? entityIdParam : null);
      setGrantDaysOnLoad(parseGrantDays(searchParams.get("days")));
      handleEntityIdClick(entityIdParam);
      setSearchParams({});
    }
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    if (!grantOnLoad || searchLoading) return;
    setGrantOnLoad(null);
    if (entityDetails) {
      handleOpenPlanModal();
      if (grantDaysOnLoad) setGrantDays(grantDaysOnLoad);
    }
  }, [grantOnLoad, searchLoading, entityDetails]);

  const handleSearch = () => {
    setCurrentPage(1);
    fetchEntitiesData({ startDate, endDate });
  };

  const handleClearFilters = () => {
    setStartDate("");
    setEndDate("");
    setCurrentPage(1);
    fetchEntitiesData({ startDate: "", endDate: "" });
  };

  const trimmedSearch = entitySearchTerm.trim();
  const canSearchEntities = trimmedSearch.length >= 3;
  const handleEntitySearch = () => {
    if (!canSearchEntities) {
      toast.info("Enter at least 3 characters to search.");
      return;
    }
    setCurrentPage(1);
    fetchEntitiesData({ search: entitySearchTerm });
  };

  const handleClearTableSearch = () => {
    setEntitySearchTerm("");
    setCurrentPage(1);
    fetchEntitiesData({ search: "" });
  };

  const fetchPayoutAccounts = async (entityId: string) => {
    try {
      setPayoutAccountsLoading(true);
      const response =
        await PaymentModuleService.getPayoutAccountsForMonitoring(
          customer_identification,
          user.entity_identification,
          entityId
        );
      setPayoutAccounts(response?.data?.element || []);
    } catch (err: any) {
      console.error("Failed to fetch payout accounts:", err);
      setPayoutAccounts([]);
    } finally {
      setPayoutAccountsLoading(false);
    }
  };

  const fetchEntityBalance = async (entityId: string) => {
    try {
      setEntityBalanceLoading(true);
      const response = await PaymentModuleService.getEntityBalanceForMonitoring(
        customer_identification,
        user.entity_identification,
        entityId
      );
      setEntityBalance(response?.data?.element || null);
    } catch (err: any) {
      console.error("Failed to fetch entity balance:", err);
      setEntityBalance(null);
    } finally {
      setEntityBalanceLoading(false);
    }
  };

  const fetchEntityPayouts = async (entityId: string) => {
    try {
      setEntityPayoutsLoading(true);
      const response = await PaymentModuleService.getEntityPayoutsForMonitoring(
        customer_identification,
        user.entity_identification,
        entityId
      );
      setEntityPayouts(response?.data?.element || []);
    } catch (err: any) {
      console.error("Failed to fetch entity payouts:", err);
      setEntityPayouts([]);
    } finally {
      setEntityPayoutsLoading(false);
    }
  };

  const fetchServiceWithPlans = async (entityId: string) => {
    try {
      setServiceWithPlansLoading(true);
      const response = await PaymentModuleService.getServiceWithPlans(
        customer_identification,
        user.entity_identification,
        entityId
      );
      setServiceWithPlans(response?.data?.element || null);
    } catch (err: any) {
      console.error("Failed to fetch service with plans:", err);
      setServiceWithPlans(null);
    } finally {
      setServiceWithPlansLoading(false);
    }
  };

  const fetchSubscriptionHistories = async (entityId: string) => {
    try {
      setSubscriptionHistoriesLoading(true);
      const response =
        await PaymentModuleService.getAllSubscriptionHistoriesGroupedByPlan(
          customer_identification,
          user.entity_identification,
          entityId
        );
      const rawHistories = response?.data?.element || [];
      const normalized: PaymentHistoryGroupedDTO[] = rawHistories.map(
        (group: any) => ({
          planCode: group.plan_code || "",
          planDescription: group.plan_description || "",
          payment_type: group.payment_type,
          paymentSteps: group.payment_steps || [],
        })
      );
      setSubscriptionHistories(normalized);
    } catch (err: any) {
      console.error("Failed to fetch subscription histories:", err);
      setSubscriptionHistories([]);
    } finally {
      setSubscriptionHistoriesLoading(false);
    }
  };

  const fetchSumPlans = async (entityId: string) => {
    try {
      setSumPlansLoading(true);
      const response = await PaymentModuleService.getSumPlanStatus(
        customer_identification,
        user.entity_identification,
        entityId
      );
      setSumPlans(response?.data?.element?.planList || []);
    } catch (err: any) {
      console.error("Failed to fetch SUM plans:", err);
      setSumPlans([]);
    } finally {
      setSumPlansLoading(false);
    }
  };

  const handleSearchEntity = async () => {
    if (!searchEntityId.trim()) {
      toast.error("Please enter an entity identification");
      return;
    }

    try {
      setSearchLoading(true);
      const response = await HttpService.get(
        `/${customer_identification}/${
          user.entity_identification
        }/system/entity?query=${searchEntityId.trim()}`
      );

      const entityData = response?.data?.element;
      setEntityDetails(entityData);
    } catch (err: any) {
      const errorMessage =
        err.response?.data?.message ||
        err.message ||
        "Failed to fetch entity details";
      toast.error(errorMessage);
      setEntityDetails(null);
    } finally {
      setSearchLoading(false);
    }
  };

  const clearEntitySections = () => {
    setPayoutAccounts(null);
    setEntityBalance(null);
    setEntityPayouts(null);
    setSubscriptionHistories(null);
    setServiceWithPlans(null);
    setSumPlans(null);
  };

  const refreshLoadedSections = (entityId: string) => {
    if (payoutAccounts !== null) fetchPayoutAccounts(entityId);
    if (entityBalance !== null) fetchEntityBalance(entityId);
    if (entityPayouts !== null) fetchEntityPayouts(entityId);
    if (subscriptionHistories !== null) fetchSubscriptionHistories(entityId);
    if (serviceWithPlans !== null) fetchServiceWithPlans(entityId);
    if (sumPlans !== null) fetchSumPlans(entityId);
  };

  const handleCloseDrawer = () => {
    setDrawerOpen(false);
    setDetailModal(null);
  };

  const handleEntityIdClick = async (entityId: string) => {
    setSearchEntityId(entityId);
    setEntityDetails(null);
    clearEntitySections();
    setDrawerOpen(true);

    try {
      setSearchLoading(true);
      const response = await HttpService.get(
        `/${customer_identification}/${user.entity_identification}/system/entity?query=${entityId}`
      );

      const entityData = response?.data?.element;
      setEntityDetails(entityData);
      if (entityData?.entity_identification) {
        fetchSumPlans(entityData.entity_identification);
      }
    } catch (err: any) {
      const errorMessage =
        err.response?.data?.message ||
        err.message ||
        "Failed to fetch entity details";
      toast.error(errorMessage);
      setEntityDetails(null);
      setDrawerOpen(false);
    } finally {
      setSearchLoading(false);
    }
  };

  const openDetailModal = (
    kind: Exclude<DetailModalKind, null>,
    loaded: unknown,
    loader: (id: string) => Promise<void>
  ) => {
    if (!entityDetails) return;
    if (loaded === null) {
      loader(entityDetails.entity_identification);
    }
    setDetailModal(kind);
  };

  const handleOpenPayoutAccounts = () =>
    openDetailModal("payoutAccounts", payoutAccounts, fetchPayoutAccounts);
  const handleOpenEntityBalance = () =>
    openDetailModal("entityBalance", entityBalance, fetchEntityBalance);
  const handleOpenEntityPayouts = () =>
    openDetailModal("entityPayouts", entityPayouts, fetchEntityPayouts);
  const handleOpenSubscriptionHistories = () =>
    openDetailModal(
      "subscriptionHistories",
      subscriptionHistories,
      fetchSubscriptionHistories
    );
  const handleOpenGuaranteeRefund = (plan: GuaranteeTargetPlan) => {
    if (!entityDetails) return;
    setGuaranteeTargetPlan(plan);
    setShowGuaranteeRefund(true);
  };

  const handleCancelPlan = (plan: GuaranteeTargetPlan) => {
    if (!entityDetails) return;
    setCancelTargetPlan(plan);
    setShowCancelModal(true);
  };

  const handleCancelConfirm = async () => {
    if (!entityDetails || !cancelTargetPlan) return;
    const planCode = cancelTargetPlan.planCode;
    const refreshedEntityId = entityDetails.entity_identification;
    try {
      setCancelSubmitting(true);
      setBusyPlanCode(planCode);
      await PaymentModuleService.cancelSumSubscriptionAtPeriodEnd(
        customer_identification,
        user.entity_identification,
        refreshedEntityId,
        planCode
      );
      toast.success("Subscription will cancel at the end of the period");
      setShowCancelModal(false);
      fetchSumPlans(refreshedEntityId);
    } catch (error: any) {
      toast.error(
        error.response?.data?.message || "Failed to cancel the subscription"
      );
    } finally {
      setCancelSubmitting(false);
      setBusyPlanCode(undefined);
    }
  };

  const handleResumePlan = async (planCode: string) => {
    if (!entityDetails) return;
    const refreshedEntityId = entityDetails.entity_identification;
    try {
      setBusyPlanCode(planCode);
      await PaymentModuleService.resumeSumSubscription(
        customer_identification,
        user.entity_identification,
        refreshedEntityId,
        planCode
      );
      toast.success("Subscription resumed");
      fetchSumPlans(refreshedEntityId);
    } catch (error: any) {
      toast.error(
        error.response?.data?.message || "Failed to resume the subscription"
      );
    } finally {
      setBusyPlanCode(undefined);
    }
  };
  const handleOpenServiceAndPlans = () => {
    fetchStripeTaxSettings();
    openDetailModal("serviceAndPlans", serviceWithPlans, fetchServiceWithPlans);
  };

  const fetchPrivatePlans = async () => {
    try {
      setPrivatePlansLoading(true);
      const response = await PaymentModuleService.getNonPublicPlans(
        customer_identification,
        user.entity_identification
      );
      setPrivatePlans(response.data.element || []);
    } catch (error: any) {
      toast.error(
        error.response?.data?.message || "Failed to fetch private plans"
      );
      setPrivatePlans([]);
    } finally {
      setPrivatePlansLoading(false);
    }
  };

  const [taxModeSavingPlanCode, setTaxModeSavingPlanCode] = useState<
    string | null
  >(null);
  const [stripeTaxSettings, setStripeTaxSettings] =
    useState<StripeTaxSettingsDTO | null>(null);

  const fetchStripeTaxSettings = async () => {
    if (stripeTaxSettings) return;
    try {
      const response = await PaymentModuleService.getStripeTaxSettings(
        customer_identification,
        user.entity_identification
      );
      setStripeTaxSettings(response.data?.element ?? null);
    } catch {
      setStripeTaxSettings(null);
    }
  };

  const handleChangePlanTaxMode = async (
    planCode: string,
    taxMode: PlanTaxMode
  ) => {
    setTaxModeSavingPlanCode(planCode);
    try {
      await PaymentModuleService.setPlanTaxMode(
        customer_identification,
        user.entity_identification,
        planCode,
        taxMode
      );
      setServiceWithPlans((current) =>
        current
          ? {
              ...current,
              plans: current.plans.map((plan) =>
                plan.plan_code === planCode
                  ? { ...plan, tax_mode: taxMode }
                  : plan
              ),
            }
          : current
      );
      toast.success(`${planCode}: ${TAX_MODE_TOASTS[taxMode]}`);
    } catch (error) {
      const message = (error as { response?: { data?: { message?: string } } })
        ?.response?.data?.message;
      toast.error(
        message ||
          (error instanceof Error
            ? error.message
            : `Could not set the tax mode of ${planCode}`)
      );
    }
    setTaxModeSavingPlanCode(null);
  };

  const handleOpenPlanModal = () => {
    setDetailModal(null);
    setShowPlanModal(true);
    setSelectedPlan("");
    setGrantDays(GRANT_DAYS_DEFAULT);
    setPlanToExpire("");
    fetchPrivatePlans();
  };

  const handleClosePlanModal = () => {
    setShowPlanModal(false);
    setSelectedPlan("");
    setGrantDays(GRANT_DAYS_DEFAULT);
    setPlanToExpire("");
  };

  const handleGrantPlan = async () => {
    if (!selectedPlan || !entityDetails) {
      toast.error("Please select a plan");
      return;
    }

    if (
      !Number.isInteger(grantDays) ||
      grantDays < GRANT_DAYS_MIN ||
      grantDays > GRANT_DAYS_MAX
    ) {
      toast.error(
        `The duration must be between ${GRANT_DAYS_MIN} and ${GRANT_DAYS_MAX} days`
      );
      return;
    }

    try {
      setGrantingPlan(true);
      const grantData: GrantPlanDTO = {
        base_plan_identification: selectedPlan,
        duration_days: grantDays,
      };

      await PaymentModuleService.grantPrivatePlan(
        customer_identification,
        user.entity_identification,
        entityDetails.entity_identification,
        grantData
      );

      toast.success("Private plan granted");
      const refreshedEntityId = entityDetails.entity_identification;
      handleClosePlanModal();
      handleSearchEntity();
      refreshLoadedSections(refreshedEntityId);
    } catch (error: any) {
      toast.error(
        error.response?.data?.message || "Failed to grant private plan"
      );
    } finally {
      setGrantingPlan(false);
    }
  };

  const handleExpirePlanClick = () => {
    if (!planToExpire || !entityDetails) {
      toast.error("Please select a plan to expire");
      return;
    }
    setShowExpireConfirmModal(true);
  };

  const handleExpirePlanConfirmed = async () => {
    if (!planToExpire || !entityDetails) {
      return;
    }

    try {
      setExpiringPlan(true);
      setShowExpireConfirmModal(false);

      await PaymentModuleService.forceExpirePlan(
        customer_identification,
        user.entity_identification,
        entityDetails.entity_identification,
        planToExpire
      );

      toast.success("Plan expired");
      const refreshedEntityId = entityDetails.entity_identification;
      setPlanToExpire("");
      handleSearchEntity();
      refreshLoadedSections(refreshedEntityId);
    } catch (error: any) {
      toast.error(error.response?.data?.message || "Failed to expire plan");
    } finally {
      setExpiringPlan(false);
    }
  };

  const handleOpenDeleteEntityModal = () => {
    setDeleteEntityConfirmText("");
    setShowDeleteEntityModal(true);
  };

  const handleCloseDeleteEntityModal = () => {
    setShowDeleteEntityModal(false);
    setDeleteEntityConfirmText("");
  };

  const handleDeleteEntityConfirmed = async () => {
    if (!entityDetails || deleteEntityConfirmText !== "CONFIRM") {
      return;
    }

    try {
      setDeletingEntity(true);
      await HttpService.delete(
        `/${customer_identification}/${user.entity_identification}/system/entity/${entityDetails.entity_identification}`
      );

      toast.success("Entity deleted");
      handleCloseDeleteEntityModal();
      handleCloseDrawer();
      setEntityDetails(null);
      clearEntitySections();
      fetchEntitiesData();
    } catch (error: any) {
      toast.error(error.response?.data?.message || "Failed to delete entity");
    } finally {
      setDeletingEntity(false);
    }
  };

  const fetchConnectionsStats = async () => {
    if (!connectionsStartDate || !connectionsEndDate) {
      toast.error("Please select both start and end dates");
      return;
    }

    try {
      setConnectionsLoading(true);
      const params = new URLSearchParams();
      params.append("start_date", new Date(connectionsStartDate).toISOString());
      params.append("end_date", new Date(connectionsEndDate).toISOString());

      const response = await HttpService.get(
        `/${customer_identification}/${
          user.entity_identification
        }/system/connections?${params.toString()}`
      );

      setConnectionsStats(response.data.element);
    } catch (err: any) {
      const errorMessage =
        err.response?.data?.message ||
        err.message ||
        "Failed to fetch connection statistics";
      toast.error(errorMessage);
      setConnectionsStats(null);
    } finally {
      setConnectionsLoading(false);
    }
  };

  const handleClearConnectionsStats = () => {
    setConnectionsStartDate("");
    setConnectionsEndDate("");
    setConnectionsStats(null);
  };

  const handleImpersonate = async (entityId: string) => {
    setImpersonateLoading(true);
    try {
      const response = await HttpService.post(
        `/${customer_identification}/${user.entity_identification}/system/entity/${entityId}/impersonate`
      );

      const oneTimeId = response?.data?.element?.one_time_id;
      if (oneTimeId && typeof oneTimeId === "string") {
        window.open(
          `/control-panel?imp=${encodeURIComponent(oneTimeId)}`,
          "_blank"
        );
      } else {
        toast.error("Could not start the impersonation session.");
      }
    } catch (error: any) {
      console.error("Failed to start impersonation:", error);
      if (error?.response?.data?.code === "RSUSKO015") {
        toast.error(
          "Cannot impersonate: the owner has not verified their email address."
        );
      } else {
        toast.error("Could not start the impersonation session.");
      }
    } finally {
      setImpersonateLoading(false);
    }
  };

  const systemApiBasePath = `/${customer_identification}/${user.entity_identification}`;

  const emailSendContext = useRootSendContext({
    rootBasePath: systemApiBasePath,
    type:
      emailModalTarget?.mode === "single"
        ? SEND_MESSAGE_TYPE.SINGLE
        : SEND_MESSAGE_TYPE.MULTI,
    toSendMsgUserId:
      emailModalTarget?.mode === "single"
        ? emailModalTarget.entityIdentification
        : "",
    rootEntityIdentifications:
      emailModalTarget?.mode === "bulk"
        ? emailModalTarget.entityIdentifications
        : [],
    show: emailModalTarget !== null,
  });

  const toggleEntitySelection = (entityId: string) => {
    setSelectedEntityIds((prev) => {
      const next = new Set(prev);
      if (next.has(entityId)) {
        next.delete(entityId);
      } else {
        next.add(entityId);
      }
      return next;
    });
  };

  const toggleSelectAllOnPage = () => {
    const pageIds = data?.entities.map((e) => e.entity_identification) ?? [];
    const allSelected =
      pageIds.length > 0 && pageIds.every((id) => selectedEntityIds.has(id));
    setSelectedEntityIds((prev) => {
      const next = new Set(prev);
      if (allSelected) {
        pageIds.forEach((id) => next.delete(id));
      } else {
        pageIds.forEach((id) => next.add(id));
      }
      return next;
    });
  };

  if (loading && !data) {
    return (
      <div className="container mx-auto py-6">
        <div className="flex justify-center items-center h-64">
          <Spinner size="xl" />
        </div>
      </div>
    );
  }

  if (error && !data) {
    return (
      <div className="container mx-auto py-6">
        <AlertMessage color="failure">
          <span className="font-medium">Error!</span> {error}
        </AlertMessage>
      </div>
    );
  }

  const featureLinks: FeatureLink[] | undefined = buildFeatureLinks(
    entityDetails?.feature_links
  );

  const shortDayMonthFormat = moment
    .localeData()
    .longDateFormat("L")
    .replace(/[/.\-\s]?Y+[/.\-\s]?/, "");

  return (
    <div className="monitoring-page">
      <Helmet>
        <title>Entities monitoring | Super User Manager</title>
        <meta
          name="description"
          content="Monitor all entities and their statistics"
        />
      </Helmet>

      <div className="container mx-auto py-6">
        <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
          <div className="min-w-0">
            <h1 className="text-xl font-semibold text-gray-900">
              Entities monitoring
            </h1>
            <p className="mt-0.5 text-sm text-gray-500">
              View and monitor all entities in the system
            </p>
          </div>
        </div>

        <section className="mb-4 rounded-lg border border-gray-200 bg-white p-4">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-end">
            <div className="flex-1">
              <Label
                htmlFor="entity-search"
                value="Search entities (ID, name, or owner email)"
                className="mb-2 block"
              />
              <div className="flex gap-2">
                <TextInput
                  id="entity-search"
                  type="text"
                  icon={HiSearch}
                  placeholder="Search by ID, name, or owner email"
                  value={entitySearchTerm}
                  onChange={(e) => setEntitySearchTerm(e.target.value)}
                  onKeyPress={(e) => e.key === "Enter" && handleEntitySearch()}
                  className="flex-1"
                />
                <CustomButton
                  onClick={handleEntitySearch}
                  disabled={loading || !canSearchEntities}
                  variant="primary"
                  title={loading ? "Searching..." : "Search"}
                  icon="search"
                />
                {entitySearchTerm && (
                  <CustomButton
                    onClick={handleClearTableSearch}
                    variant="secondary"
                    title="Clear"
                  />
                )}
              </div>
            </div>

            <div className="flex items-end">
              <CustomButton
                onClick={() => setShowFilters((v) => !v)}
                variant="secondary"
                comp={
                  <span className="flex items-center gap-2 text-sm font-medium text-gray-700">
                    <HiFilter size={18} />
                    Filters
                    {showFilters ? (
                      <HiChevronUp size={16} />
                    ) : (
                      <HiChevronDown size={16} />
                    )}
                  </span>
                }
              />
            </div>
          </div>

          {showFilters && (
            <div className="mt-4 grid grid-cols-1 gap-4 border-t pt-4 md:grid-cols-2 lg:grid-cols-4">
              <div>
                <Label
                  htmlFor="start-date"
                  value="Start date"
                  className="mb-2 block"
                />
                <TextInput
                  id="start-date"
                  type="datetime-local"
                  icon={HiCalendar}
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                />
              </div>

              <div>
                <Label
                  htmlFor="end-date"
                  value="End date"
                  className="mb-2 block"
                />
                <TextInput
                  id="end-date"
                  type="datetime-local"
                  icon={HiCalendar}
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                />
              </div>

              <div>
                <Label
                  htmlFor="limit"
                  value="Entities per page"
                  className="mb-2 block"
                />
                <TextInput
                  id="limit"
                  type="number"
                  min="1"
                  max="100"
                  value={limit}
                  onChange={(e) => setLimit(parseInt(e.target.value) || 10)}
                />
              </div>

              <div className="flex items-end gap-2">
                <CustomButton
                  onClick={handleSearch}
                  disabled={loading}
                  variant="primary"
                  title="Apply"
                  icon="search"
                />
                <CustomButton
                  onClick={handleClearFilters}
                  disabled={loading}
                  variant="secondary"
                  title="Clear"
                />
              </div>
            </div>
          )}
        </section>

        <section className="mb-4 rounded-lg border border-gray-200 bg-white p-4">
          <button
            type="button"
            onClick={() => setShowConnectionsStats((v) => !v)}
            className="flex w-full items-center justify-between text-left"
          >
            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                Connection statistics
              </h2>
              <p className="mt-0.5 text-sm text-gray-500">
                View the number of connections within a date range
              </p>
            </div>
            {showConnectionsStats ? (
              <HiChevronUp className="text-gray-500" size={22} />
            ) : (
              <HiChevronDown className="text-gray-500" size={22} />
            )}
          </button>

          {showConnectionsStats && (
            <div className="mt-4">
              <div className="mb-4 grid grid-cols-1 gap-4 md:grid-cols-3">
                <div>
                  <Label
                    htmlFor="connections-start-date"
                    value="Start date"
                    className="mb-2 block"
                  />
                  <TextInput
                    id="connections-start-date"
                    type="datetime-local"
                    value={connectionsStartDate}
                    onChange={(e) => setConnectionsStartDate(e.target.value)}
                  />
                </div>

                <div>
                  <Label
                    htmlFor="connections-end-date"
                    value="End date"
                    className="mb-2 block"
                  />
                  <TextInput
                    id="connections-end-date"
                    type="datetime-local"
                    value={connectionsEndDate}
                    onChange={(e) => setConnectionsEndDate(e.target.value)}
                  />
                </div>

                <div className="flex items-end gap-2">
                  <CustomButton
                    onClick={fetchConnectionsStats}
                    disabled={connectionsLoading}
                    variant="primary"
                    title={connectionsLoading ? "Loading..." : "Search"}
                    icon="search"
                  />
                  {connectionsStats && (
                    <CustomButton
                      onClick={handleClearConnectionsStats}
                      variant="secondary"
                      title="Clear"
                    />
                  )}
                </div>
              </div>

              {connectionsStats && (
                <div className="mt-4 rounded-lg border border-gray-200 bg-gray-50 p-4">
                  <h3 className="mb-3 text-sm font-semibold text-gray-900">
                    Summary
                  </h3>
                  <div className="mb-4 grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
                    <div className="rounded-lg border border-gray-200 bg-white p-4">
                      <p className="mb-1 text-xs text-gray-500">
                        Total connections
                      </p>
                      <p className="text-xl font-semibold tabular-nums text-gray-900">
                        {connectionsStats.total_connections}
                      </p>
                    </div>
                    <div className="rounded-lg border border-gray-200 bg-white p-4">
                      <p className="mb-1 text-xs text-gray-500">Start date</p>
                      <p className="text-sm tabular-nums text-gray-900">
                        {formatDate(connectionsStats.filter.start_date)}
                      </p>
                    </div>
                    <div className="rounded-lg border border-gray-200 bg-white p-4">
                      <p className="mb-1 text-xs text-gray-500">End date</p>
                      <p className="text-sm tabular-nums text-gray-900">
                        {formatDate(connectionsStats.filter.end_date)}
                      </p>
                    </div>
                    <div className="rounded-lg border border-gray-200 bg-white p-4">
                      <p className="mb-1 text-xs text-gray-500">
                        Days in range
                      </p>
                      <p className="text-xl font-semibold tabular-nums text-gray-900">
                        {connectionsStats.connections_by_date.length}
                      </p>
                    </div>
                  </div>

                  {connectionsStats.connections_by_date.length > 0 && (
                    <div className="mt-4">
                      <h4 className="mb-4 text-sm font-semibold text-gray-900">
                        Connections over time
                      </h4>
                      <div className="relative h-80 w-full">
                        <ResponsiveContainer width="100%" height="100%">
                          <AreaChart
                            data={connectionsStats.connections_by_date.map(
                              (item) => ({
                                name: moment(item.date).format(
                                  shortDayMonthFormat
                                ),
                                connections: item.connections,
                                fullDate: moment(item.date).format("L"),
                              })
                            )}
                            margin={{
                              top: 20,
                              right: 30,
                              left: 20,
                              bottom: 40,
                            }}
                          >
                            <defs>
                              <linearGradient
                                id="colorConnections"
                                x1="0"
                                y1="0"
                                x2="0"
                                y2="1"
                              >
                                <stop
                                  offset="5%"
                                  stopColor={CHART.series}
                                  stopOpacity={0.3}
                                />
                                <stop
                                  offset="95%"
                                  stopColor={CHART.series}
                                  stopOpacity={0}
                                />
                              </linearGradient>
                            </defs>
                            <CartesianGrid
                              strokeDasharray="3 3"
                              stroke={CHART.grid}
                            />
                            <XAxis
                              dataKey="name"
                              axisLine={false}
                              tickLine={false}
                              tick={{ fontSize: 12, fill: CHART.axis }}
                            >
                              <ChartLabel
                                value="Date"
                                position="insideBottom"
                                offset={-10}
                                style={{
                                  textAnchor: "middle",
                                  fontSize: "12px",
                                  fontWeight: "500",
                                  fill: CHART.axisLabel,
                                }}
                              />
                            </XAxis>
                            <YAxis
                              axisLine={false}
                              tickLine={false}
                              domain={[0, "dataMax + 1"]}
                              allowDecimals={false}
                              tick={{ fontSize: 12, fill: CHART.axis }}
                            >
                              <ChartLabel
                                value="Connections"
                                angle={-90}
                                position="insideLeft"
                                style={{
                                  textAnchor: "middle",
                                  fontSize: "12px",
                                  fontWeight: "500",
                                  fill: CHART.axisLabel,
                                }}
                              />
                            </YAxis>
                            <Tooltip
                              contentStyle={{
                                backgroundColor: CHART.tooltipBg,
                                color: CHART.tooltipText,
                                border: "none",
                                borderRadius: "8px",
                                boxShadow:
                                  "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)",
                              }}
                              labelStyle={{ color: CHART.tooltipText }}
                              formatter={(value: any) => [value, "Connections"]}
                            />
                            <Area
                              type="monotone"
                              dataKey="connections"
                              stroke={CHART.series}
                              strokeWidth={2}
                              fill="url(#colorConnections)"
                              dot={{ fill: CHART.series, strokeWidth: 2, r: 4 }}
                              activeDot={{
                                r: 6,
                                stroke: CHART.series,
                                strokeWidth: 2,
                                fill: CHART.surface,
                              }}
                            />
                          </AreaChart>
                        </ResponsiveContainer>
                      </div>

                      <div className="mt-4 flex items-center gap-3">
                        <div className="flex items-center gap-2">
                          <div className="h-3 w-3 rounded-full bg-green-500" />
                          <span className="text-sm font-medium text-gray-700">
                            Connections
                          </span>
                        </div>
                      </div>
                    </div>
                  )}

                  {connectionsStats.connections_by_date.length === 0 && (
                    <div className="mt-4 text-center text-sm text-gray-500">
                      No connection data for the selected date range
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </section>

        {data && (
          <section className="rounded-lg border border-gray-200 bg-white p-4">
            <div className="mb-4 flex flex-wrap gap-4 text-sm text-gray-500">
              <span>
                Total entities{" "}
                <span className="font-medium tabular-nums text-gray-900">
                  {data.pagination.total_entities}
                </span>
              </span>
              <span>
                Page{" "}
                <span className="font-medium tabular-nums text-gray-900">
                  {data.pagination.current_page} / {data.pagination.total_pages}
                </span>
              </span>
            </div>

            {selectedEntityIds.size > 0 && (
              <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-blue-200 bg-blue-50 px-4 py-3">
                <span className="text-sm font-medium text-blue-900">
                  To the owners of {selectedEntityIds.size} selected entities
                </span>
                <div className="flex gap-2">
                  <CustomButton
                    onClick={() => setSelectedEntityIds(new Set())}
                    variant="secondary"
                    title="Cancel"
                  />
                  <CustomButton
                    onClick={() =>
                      setEmailModalTarget({
                        mode: "bulk",
                        entityIdentifications: Array.from(selectedEntityIds),
                      })
                    }
                    variant="primary"
                    icon="envelop"
                    title={`Email ${selectedEntityIds.size} owners`}
                  />
                </div>
              </div>
            )}

            <DataTable wrapperClassName="border-0">
              <DataTable.Head>
                <DataTable.HeadCell className="w-4 px-4 py-3">
                  <Checkbox
                    checked={
                      (data.entities.length ?? 0) > 0 &&
                      data.entities.every((e) =>
                        selectedEntityIds.has(e.entity_identification)
                      )
                    }
                    onChange={toggleSelectAllOnPage}
                    aria-label="Select all entities on this page"
                  />
                </DataTable.HeadCell>
                <DataTable.HeadCell>Entity</DataTable.HeadCell>
                <DataTable.HeadCell>Status</DataTable.HeadCell>
                <DataTable.HeadCell>Users</DataTable.HeadCell>
                <DataTable.HeadCell>Features</DataTable.HeadCell>
                <DataTable.HeadCell>Creation date</DataTable.HeadCell>
              </DataTable.Head>
              <DataTable.Body>
                {data.entities.length === 0 ? (
                  <DataTable.Row>
                    <DataTable.Cell colSpan={6}>
                      <EmptyState
                        icon={<HiSearch size={24} />}
                        title="No entities found"
                      />
                    </DataTable.Cell>
                  </DataTable.Row>
                ) : (
                  data.entities.map((entity) => (
                    <DataTable.Row
                      key={entity.entity_identification}
                      className="cursor-pointer transition-colors"
                      onClick={() =>
                        handleEntityIdClick(entity.entity_identification)
                      }
                      title="Click to view details"
                    >
                      <DataTable.Cell
                        className="w-4 px-4 py-4"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Checkbox
                          checked={selectedEntityIds.has(
                            entity.entity_identification
                          )}
                          onChange={() =>
                            toggleEntitySelection(entity.entity_identification)
                          }
                          aria-label={`Select ${entity.entity_name}`}
                        />
                      </DataTable.Cell>
                      <DataTable.Cell className="whitespace-nowrap">
                        <div className="font-medium text-gray-900">
                          {entity.entity_name}
                        </div>
                        <div className="text-xs text-gray-400">
                          <NoTranslate>
                            {entity.entity_identification}
                          </NoTranslate>
                        </div>
                      </DataTable.Cell>
                      <DataTable.Cell>
                        {getStatusDot(entity.entity_status)}
                      </DataTable.Cell>
                      <DataTable.Cell className="tabular-nums text-gray-900">
                        {entity.active_users}
                      </DataTable.Cell>
                      <DataTable.Cell>
                        <FeatureBadges features={entity.features} />
                      </DataTable.Cell>
                      <DataTable.Cell className="whitespace-nowrap text-xs tabular-nums text-gray-600">
                        {formatDate(entity.creation_date)}
                      </DataTable.Cell>
                    </DataTable.Row>
                  ))
                )}
              </DataTable.Body>
            </DataTable>

            {data.pagination.total_pages > 1 && (
              <div className="mt-4 flex flex-col items-center justify-between gap-3 border-t border-gray-200 pt-4 sm:flex-row">
                <CustomButton
                  variant="secondary"
                  onClick={() =>
                    setCurrentPage((prev) => Math.max(1, prev - 1))
                  }
                  disabled={!data.pagination.has_previous_page || loading}
                  className="w-full sm:w-auto"
                  title="Previous"
                />

                <div className="flex flex-wrap justify-center gap-2">
                  {Array.from(
                    { length: Math.min(5, data.pagination.total_pages) },
                    (_, i) => {
                      let pageNumber;
                      if (data.pagination.total_pages <= 5) {
                        pageNumber = i + 1;
                      } else if (currentPage <= 3) {
                        pageNumber = i + 1;
                      } else if (
                        currentPage >=
                        data.pagination.total_pages - 2
                      ) {
                        pageNumber = data.pagination.total_pages - 4 + i;
                      } else {
                        pageNumber = currentPage - 2 + i;
                      }

                      return (
                        <CustomButton
                          key={pageNumber}
                          size="sm"
                          variant={
                            currentPage === pageNumber ? "primary" : "secondary"
                          }
                          onClick={() => setCurrentPage(pageNumber)}
                          disabled={loading}
                          title={pageNumber}
                        />
                      );
                    }
                  )}
                </div>

                <CustomButton
                  variant="secondary"
                  onClick={() =>
                    setCurrentPage((prev) =>
                      Math.min(data.pagination.total_pages, prev + 1)
                    )
                  }
                  disabled={!data.pagination.has_next_page || loading}
                  className="w-full sm:w-auto"
                  title="Next"
                />
              </div>
            )}
          </section>
        )}
      </div>

      <EntityDrawer
        open={drawerOpen}
        onClose={handleCloseDrawer}
        loading={searchLoading}
        entityDetails={entityDetails}
        featureLinks={featureLinks}
        onEmailOwner={() =>
          entityDetails &&
          setEmailModalTarget({
            mode: "single",
            entityIdentification: entityDetails.entity_identification,
          })
        }
        onImpersonate={() =>
          entityDetails &&
          handleImpersonate(entityDetails.entity_identification)
        }
        impersonateLoading={impersonateLoading}
        onGrantAccess={handleOpenPlanModal}
        onGuaranteeRefund={handleOpenGuaranteeRefund}
        onCancelPlan={handleCancelPlan}
        onResumePlan={handleResumePlan}
        busyPlanCode={busyPlanCode}
        onDelete={handleOpenDeleteEntityModal}
        onOpenPayoutAccounts={handleOpenPayoutAccounts}
        onOpenEntityBalance={handleOpenEntityBalance}
        onOpenEntityPayouts={handleOpenEntityPayouts}
        onOpenSubscriptionHistories={handleOpenSubscriptionHistories}
        onOpenServiceAndPlans={handleOpenServiceAndPlans}
        payoutAccounts={payoutAccounts}
        payoutAccountsLoading={payoutAccountsLoading}
        entityBalance={entityBalance}
        entityBalanceLoading={entityBalanceLoading}
        entityPayouts={entityPayouts}
        entityPayoutsLoading={entityPayoutsLoading}
        subscriptionHistories={subscriptionHistories}
        subscriptionHistoriesLoading={subscriptionHistoriesLoading}
        serviceWithPlans={serviceWithPlans}
        serviceWithPlansLoading={serviceWithPlansLoading}
        sumPlans={sumPlans}
        sumPlansLoading={sumPlansLoading}
      />

      <EntityDetailModals
        kind={detailModal}
        onClose={() => setDetailModal(null)}
        entityName={entityDetails?.entity_name ?? ""}
        payoutAccounts={payoutAccounts}
        payoutAccountsLoading={payoutAccountsLoading}
        entityBalance={entityBalance}
        entityBalanceLoading={entityBalanceLoading}
        entityPayouts={entityPayouts}
        entityPayoutsLoading={entityPayoutsLoading}
        subscriptionHistories={subscriptionHistories}
        subscriptionHistoriesLoading={subscriptionHistoriesLoading}
        serviceWithPlans={serviceWithPlans}
        serviceWithPlansLoading={serviceWithPlansLoading}
        onChangePlanTaxMode={handleChangePlanTaxMode}
        taxModeSavingPlanCode={taxModeSavingPlanCode}
        stripeTaxSettings={stripeTaxSettings}
        onOpenPlanModal={handleOpenPlanModal}
      />

      <PlanManagementModal
        show={showPlanModal}
        onClose={handleClosePlanModal}
        entityName={entityDetails?.entity_name}
        entityId={entityDetails?.entity_identification}
        privatePlans={privatePlans}
        privatePlansLoading={privatePlansLoading}
        selectedPlan={selectedPlan}
        onSelectedPlanChange={setSelectedPlan}
        durationDays={grantDays}
        onDurationDaysChange={setGrantDays}
        grantingPlan={grantingPlan}
        onGrantPlan={handleGrantPlan}
        planToExpire={planToExpire}
        onPlanToExpireChange={setPlanToExpire}
        expiringPlan={expiringPlan}
        onExpirePlanClick={handleExpirePlanClick}
      />

      <GuaranteeRefundModal
        show={showGuaranteeRefund}
        onClose={() => setShowGuaranteeRefund(false)}
        customerId={customer_identification}
        rootEntityId={user.entity_identification}
        entityId={entityDetails?.entity_identification}
        entityName={entityDetails?.entity_name}
        plan={guaranteeTargetPlan}
        onRefunded={() =>
          entityDetails && fetchSumPlans(entityDetails.entity_identification)
        }
      />

      <CancelSubscriptionModal
        show={showCancelModal}
        onClose={() => setShowCancelModal(false)}
        planDescription={cancelTargetPlan?.planDescription}
        planCode={cancelTargetPlan?.planCode}
        entityName={entityDetails?.entity_name}
        submitting={cancelSubmitting}
        onConfirm={handleCancelConfirm}
      />

      <ConfirmModal
        show={showExpireConfirmModal}
        onClose={setShowExpireConfirmModal}
        title="Expire private plan"
        description={`Are you sure you want to expire plan ${planToExpire} for entity ${
          entityDetails?.entity_name ?? ""
        }?`}
        cancelBtnName="Cancel"
        cancelBtnFn={setShowExpireConfirmModal}
        confirmBtnName="Expire plan"
        confirmBtnFn={handleExpirePlanConfirmed}
        disabled={expiringPlan}
        isLoading={expiringPlan}
        ConfirmBtnColor="red"
      />

      <ConfirmModal
        show={showDeleteEntityModal}
        onClose={(v) => {
          if (!v) handleCloseDeleteEntityModal();
        }}
        title="Delete entity"
        description={
          <div className="flex flex-col gap-3">
            <p>
              You are about to permanently delete the entity{" "}
              <span className="font-semibold">
                {entityDetails?.entity_name}
              </span>{" "}
              (
              <span className="font-mono text-xs">
                {entityDetails?.entity_identification}
              </span>
              ). This action cannot be undone.
            </p>
            <p className="text-left">
              To proceed, type <span className="font-semibold">CONFIRM</span>
            </p>
            <TextInput
              type="text"
              value={deleteEntityConfirmText}
              onChange={(e) => setDeleteEntityConfirmText(e.target.value)}
              onPaste={(e) => e.preventDefault()}
              autoComplete="off"
              placeholder="CONFIRM"
            />
          </div>
        }
        cancelBtnName="Cancel"
        cancelBtnFn={() => handleCloseDeleteEntityModal()}
        confirmBtnName="Delete entity"
        confirmBtnFn={handleDeleteEntityConfirmed}
        disabled={deleteEntityConfirmText !== "CONFIRM" || deletingEntity}
        isLoading={deletingEntity}
        ConfirmBtnColor="red"
        icon={<HiTrash size={24} className="text-red-600" />}
        iconBgColor="bg-red-100"
      />

      <SendPersonalMessage
        show={emailModalTarget !== null}
        ctrFn={(v) => {
          const next = typeof v === "function" ? v(true) : v;
          if (!next) setEmailModalTarget(null);
        }}
        type={
          emailModalTarget?.mode === "single"
            ? SEND_MESSAGE_TYPE.SINGLE
            : SEND_MESSAGE_TYPE.MULTI
        }
        sendContext={emailSendContext}
        onResolved={() => {
          if (emailModalTarget?.mode === "bulk") {
            setSelectedEntityIds(new Set());
          }
          setEmailModalTarget(null);
        }}
      />
    </div>
  );
};

export default Monitoring;
